import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useContext, useEffect, useMemo } from 'react'
import clsx from 'clsx'
import FooterRoutes from './FooterRoutes'
import App from 'components/App/App'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import GlobalStyles from 'styles/GlobalStyles'
import styled from 'styled-components'
import BannerRoutes from './BannerRoutes'
import NotificationBannerRoutes from './NotificationBannerRoutes'
import ModalPopupsGate from '../ModalPopups/ModalPopupsGate'
import useLabV2Snackbar from '../LabSnackbar/useLabV2Snackbar'
import useLabV2Modal from '../ModalPopups/useLabV2Modal'
import { LAB_CAMPAIGN_PREVIEW_QUERY_PARAM } from 'constants/lab'
import LabUiPreviewOverlay from '../LabUiPreview/LabUiPreviewOverlay'
import StatusMatchEnrolledModalGate from 'loyalty/components/StatusMatchEnrolledModalGate'
import AutoEnrolledNoticeGate from 'loyalty/components/AutoEnrolledNoticeGate'
import GhaLoadingDiagnostics from 'components/Common/GhaLoadingDiagnostics'
import HeaderRoutes from './HeaderRoutes'
import CookieSettingsHeaderAccess from 'components/Consent/CookieSettingsHeaderAccess'
import { useCanShowPostConsentPrompts } from '../../../consent/waitingForConsent'
import PageRoutes from './PageRoutes'
import { connect } from 'react-redux'
import GeoContext from 'contexts/geoContext'
import { fetchGeoAirports, fetchGeoState } from 'actions/GeoActions'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ErrorPage from 'components/Pages/ErrorPage'
import { useAppDispatch } from 'hooks/reduxHooks'
import GlobalSearchV2 from 'components/SearchV2/GlobalSearchV2'
import { fetchDepositConfig } from 'actions/SystemActions'
import SupportAssistantChatWidgetRoutes from 'supportAssistant/routes/SupportAssistantChatWidgetRoutes'
import AccountAccessPrompt from 'components/Account/AccountAccessPrompt/AccountAccessPrompt'
import UnhandledErrorPage from 'components/Pages/ErrorPage/UnhandledErrorPage'
import config from 'constants/config'
import { useRestoreCartState } from 'checkout/hooks/useRestoreCartState'
import PromoDisplayPrompt from 'marketing/components/PromoDisplayPrompt'
import ReferralCodePrompt from 'marketing/components/ReferralCodePrompt'
import VoucherPopupPrompt from 'marketing/components/VoucherPopupPrompt'
import usePushNotificationPermission from 'hooks/Notifications/usePushNotificationPermission'
import loadable from '@loadable/component'
import SalesCopilotLayout from 'components/App/SalesCopilot/SalesCopilotLayout'
import getOverridePromoDisplayPrompt from 'marketing/components/getOverridePromoDisplayPrompt'
import useHeadlessModeBodyClass from 'hooks/useHeadlessModeBodyClass'
import usePaletteVersionBodyClass from 'hooks/usePaletteVersionBodyClass'
import { getRegionCodeFromPathname } from 'lib/string/regionUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import { useIntl } from 'react-intl'
import AuthBackedLocalePreferenceSync from './AuthBackedLocalePreferenceSync'
import { AssistantSurfaceLayoutProvider } from 'tripPlanner/assistant/layout/AssistantSurfaceLayoutContext'
import ExperimentControlPanelLauncher from 'components/DevTools/ExperimentControlPanel/ExperimentControlPanelLauncher'
import { ActiveVouchersProvider } from 'marketing/contexts/ActiveVouchersContext'
import { FlightsSearchEditProvider } from 'contexts/Flights/FlightsSearchEdit/FlightsSearchEditContext'
import SearchDrawerTimingProvider from 'hooks/GlobalSearch/SearchDrawerTimingProvider'
import { isAgentHubOidcCallbackPath } from 'agentHub/routes/AgentHubRoutes'

const SpoofWarning = loadable(() => import('../SpoofWarning'))
const ChannelMarkupStaffBanner = loadable(() => import('../ChannelMarkupStaffBanner/ChannelMarkupStaffBanner'))
const SplashPage = loadable(() => import('components/Pages/SplashPage'))
const AgentHubOidcCallback = loadable(() => import('agentHub/pages/AgentHubOidcCallback'))

const fixedHeightRoutes = [
  '/:regionCode/search/map',
  '/:regionCode/search/store/map',
  '/:regionCode/search/homes-and-villas/map',
  '/:regionCode/search/experiences/map',
  '/:regionCode/support',
]

const ACTIVE_VOUCHERS_CHECKOUT_ROUTES = [
  '/:regionCode/checkout/*',
  '/:regionCode/checkout-gateway/*',
  '/:regionCode/checkout-restore-cart/*',
]

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(var(---vh) * 100);

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    overflow-y: auto;
  }

  &.fixed-height {
    /*
      For pages such as map views where there's no page scrolling.
      Using position: absolute and height: 100%, rather than e.g. height: 100vh
      helps it work better with mobile browsers that cover part of the viewport
      with menu bars. I'm looking at YOU, iOS Safari and iOS Chrome >:(
    */
    position: absolute;
    min-height: 1px;
    height: 100%;
    width: 100%;
  }
`

interface Props {
  pathname: string
  headlessMode: boolean
  isSpoofed?: boolean
}

function Routes(props: Props) {
  const { isSpoofed, headlessMode, pathname } = props
  const geo = useContext(GeoContext)
  const intl = useIntl()

  const dispatch = useAppDispatch()
  const isFixedHeightRoute = useMemo(() => {
    return fixedHeightRoutes.some(route => matchLocaleAwarePath(pathname, route))
  }, [pathname])
  const routeRegionCode = useMemo(() => getRegionCodeFromPathname(pathname) ?? '', [pathname])
  const regionCodeToLoad = routeRegionCode || geo.currentRegionCode || config.defaultRegion

  useRestoreCartState()
  usePushNotificationPermission()
  useHeadlessModeBodyClass(headlessMode)
  usePaletteVersionBodyClass()
  const location = useLocation()
  const previewParams = new URLSearchParams(location.search)
  const isLabUiPreview = !!previewParams.get(LAB_CAMPAIGN_PREVIEW_QUERY_PARAM)
  useLabV2Snackbar({ enabled: !headlessMode && !isLabUiPreview })
  useLabV2Modal({ enabled: !headlessMode && !isLabUiPreview })
  useEffect(() => {
    dispatch(fetchGeoState(regionCodeToLoad, true))
    dispatch(fetchGeoAirports())
    dispatch(fetchDepositConfig())
  }, [dispatch, regionCodeToLoad])

  const overridePromoDisplayPrompt = getOverridePromoDisplayPrompt(intl, pathname, location.search)
  const isSplashPage = !!matchLocaleAwarePath(pathname, '/:regionCode/splashpage')
  // Standalone, so the logged-in header, prompts and hub routes can't render over it before its reload.
  const isOidcCallbackPage = isAgentHubOidcCallbackPath(pathname)
  const isRegionRoute = !!matchLocaleAwarePath(pathname, '/:regionCode')
  const canShowPostConsentPrompts = useCanShowPostConsentPrompts() && !isLabUiPreview
  const shouldPrefetchActiveVouchers =
    (config.APPLY_VOUCHER_ENABLED && !!matchLocaleAwarePath(pathname, ACTIVE_VOUCHERS_CHECKOUT_ROUTES)) ||
    (config.ENABLE_VOUCHER_PAGE && !!matchLocaleAwarePath(pathname, '/:regionCode/account/vouchers')) ||
    (config.ENABLE_MY_VOUCHERS_HOMEPAGE_CAROUSEL && !!matchLocaleAwarePath(pathname, {
      exact: true,
      path: '/:regionCode',
    }))

  return (
    <ErrorBoundary>
      <AssistantSurfaceLayoutProvider>
        {isLabUiPreview && <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>}
        <AuthBackedLocalePreferenceSync />
        <ExperimentControlPanelLauncher />
        <AppWrapper className={clsx({ 'fixed-height': isFixedHeightRoute })}>
          {isSplashPage && <SplashPage />}
          {isSplashPage && <CookieSettingsHeaderAccess />}
          {isOidcCallbackPage && <AgentHubOidcCallback />}
          {!isSplashPage && !isOidcCallbackPage && <>
            {!geo.initialised && <LoadingIndicator />}
            {geo.initialised && <>
              {geo.fetchRegionError && <ErrorPage error={geo.fetchRegionError} />}
              {!geo.fetchRegionError && (
                <SearchDrawerTimingProvider>
                  <ActiveVouchersProvider prefetch={shouldPrefetchActiveVouchers}>
                    {isSpoofed && <SpoofWarning />}
                    <ChannelMarkupStaffBanner />
                    <App>
                      {!headlessMode && isRegionRoute && <BannerRoutes />}
                      {(config.notification && !headlessMode && isRegionRoute) && <NotificationBannerRoutes />}
                      <FlightsSearchEditProvider>
                        {!headlessMode && isRegionRoute && <HeaderRoutes />}
                        {isRegionRoute && <CookieSettingsHeaderAccess />}
                        {!headlessMode && canShowPostConsentPrompts && <ReferralCodePrompt />}
                        {!headlessMode && !isLabUiPreview && <ModalPopupsGate />}
                        {!headlessMode && canShowPostConsentPrompts && <StatusMatchEnrolledModalGate />}
                        {!headlessMode && canShowPostConsentPrompts && <AutoEnrolledNoticeGate />}
                        {!headlessMode && canShowPostConsentPrompts && <PromoDisplayPrompt overridePromoDisplayPrompt={overridePromoDisplayPrompt} />}
                        {canShowPostConsentPrompts && <VoucherPopupPrompt />}
                        {!headlessMode && canShowPostConsentPrompts && !config.ENABLE_ACCOUNT_ACCESS_PROMPT && <AccountAccessPrompt />}
                        <SalesCopilotLayout>
                          <ErrorBoundary fallback={() => <UnhandledErrorPage />}>
                            {isRegionRoute && <PageRoutes />}
                          </ErrorBoundary>
                        </SalesCopilotLayout>
                      </FlightsSearchEditProvider>
                      {!headlessMode && isLabUiPreview && <LabUiPreviewOverlay />}
                    </App>
                    {!headlessMode && isRegionRoute && <FooterRoutes />}
                    {/* This needs to be top level so mobile search has the components available in headless mode */}
                    <GlobalSearchV2 />
                    {isRegionRoute && <SupportAssistantChatWidgetRoutes />}
                  </ActiveVouchersProvider>
                </SearchDrawerTimingProvider>
              )}
            </>}
          </>}
          {/* Outside load-state branches so it can report on them. */}
          <GhaLoadingDiagnostics key={pathname} />
          <GlobalStyles />
        </AppWrapper>
      </AssistantSurfaceLayoutProvider>
    </ErrorBoundary>
  )
}

function mapStateToProps(state: App.State) {
  return {
    pathname: state.router.location.pathname,
    headlessMode: state.system.headlessMode,
    isSpoofed: state.auth.account.isSpoofed,
  }
}

export default connect(mapStateToProps)(Routes)
